// Lightweight chat helpers: text formatting, timestamps, and avatars.

export function formatClock(iso) {
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return "";
  }
}

export function fullTimestamp(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch (e) {
    return "";
  }
}

// Relative time for recent messages, absolute clock beyond an hour.
export function timeLabel(iso, nowMs) {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const diff = Math.max(0, (nowMs || Date.now()) - t);
  const s = Math.floor(diff / 1000);
  if (s < 10) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min${m > 1 ? "s" : ""} ago`;
  return formatClock(iso);
}

// Renders text with **bold**, *italic*, `code`, clickable links, and @mentions.
export function FormattedText({ text, mentionNames = [], myName }) {
  if (!text) return null;
  const nodes = [];
  const re = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*)|(https?:\/\/[^\s]+)|(@\w+)/g;
  const nameSet = new Set((mentionNames || []).map((n) => (n || "").toLowerCase()));
  let last = 0;
  let key = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (m[1]) {
      nodes.push(<code key={key++} className="px-1 py-0.5 rounded bg-black/40 border border-white/10 text-[0.85em] font-mono">{tok.slice(1, -1)}</code>);
    } else if (m[2]) {
      nodes.push(<strong key={key++} className="font-bold">{tok.slice(2, -2)}</strong>);
    } else if (m[3]) {
      nodes.push(<em key={key++} className="italic">{tok.slice(1, -1)}</em>);
    } else if (m[4]) {
      nodes.push(<a key={key++} href={tok} target="_blank" rel="noopener noreferrer" className="underline text-[#8ab4ff] break-all hover:text-[#a9c7ff]">{tok}</a>);
    } else if (m[5]) {
      const nm = tok.slice(1);
      const isMe = myName && nm.toLowerCase() === myName.toLowerCase();
      const known = nameSet.has(nm.toLowerCase());
      nodes.push(
        <span key={key++} className={`font-semibold ${isMe ? "bg-[#00D9FF]/30 text-white px-1 rounded" : known ? "text-[#00D9FF]" : "text-[#8ab4ff]"}`}>@{nm}</span>
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return <>{nodes}</>;
}

export function Avatar({ msg, size = 28, onClick }) {
  const dim = { width: size, height: size };
  const clickable = !!onClick;
  const base = `shrink-0 rounded-full flex items-center justify-center overflow-hidden ${clickable ? "cursor-pointer hover:ring-2 hover:ring-[#00D9FF]/60 transition-all" : ""}`;
  if (msg.isHost) {
    return (
      <span data-testid="chat-avatar" style={dim} className={`${base} bg-[#00D9FF] text-navy-ink`}>
        <span style={{ fontSize: size * 0.5 }}>🎬</span>
      </span>
    );
  }
  const emoji = msg.avatarEmoji;
  const color = msg.avatarColor;
  const inner = emoji || msg.flag || "🙂";
  return (
    <span
      data-testid="chat-avatar"
      onClick={onClick}
      title={onClick ? `${msg.name} · ${msg.country || ""}` : undefined}
      style={{ ...dim, backgroundColor: color || "#1b1b26" }}
      className={`${base} border border-white/10`}
    >
      <span style={{ fontSize: size * 0.5 }}>{inner}</span>
    </span>
  );
}
